import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { createFeature, createReducer, on } from '@ngrx/store';

import { SimulationCharacteristics } from '@features/loan/interfaces/simulation-characteristics.interface';
import { Catalog, LoanCharacteristics } from '@features/loan/models';
import { PublishablesAreas } from '@features/loan/models/publishables-areas-response.model';

import * as fromActions from '../actions';

export const featureName = 'loan';

export interface PpiSimulatorState {
  catalogLoadingStatus: LoadingStatus;
  loanCreationLoadingStatus: LoadingStatus;
  connectedSimulationLoadingStatus: LoadingStatus;
  notConnectedSimulationLoadingStatus: LoadingStatus;
  saveSimulationLoadingStatus: LoadingStatus;
  areas: PublishablesAreas;
  jointCaseLoanCreationLoadingStatus: LoadingStatus;
  jointCaseGetSimulationLoadingStatus: LoadingStatus;
  catalog: Catalog;
  creationResponse: LoanCharacteristics;
  simulationCharacteristics: SimulationCharacteristics;
  isConnected: boolean | undefined;
  calculationByMonthlyPayment: boolean | undefined;
  selectedProjectType: string | undefined;
  jointCaseCreationResponse: LoanCharacteristics;
  jointCaseSimulationCharacteristics: SimulationCharacteristics;
  isSingleBorrower: boolean;
}

export const initialState: PpiSimulatorState = {
  catalogLoadingStatus: initialStatus,
  loanCreationLoadingStatus: initialStatus,
  connectedSimulationLoadingStatus: initialStatus,
  notConnectedSimulationLoadingStatus: initialStatus,
  saveSimulationLoadingStatus: initialStatus,
  areas: new PublishablesAreas(),
  jointCaseLoanCreationLoadingStatus: initialStatus,
  jointCaseGetSimulationLoadingStatus: initialStatus,
  catalog: new Catalog(),
  creationResponse: new LoanCharacteristics(),
  simulationCharacteristics: {} as SimulationCharacteristics,
  isConnected: undefined,
  calculationByMonthlyPayment: undefined,
  selectedProjectType: undefined,
  jointCaseCreationResponse: new LoanCharacteristics(),
  jointCaseSimulationCharacteristics: {} as SimulationCharacteristics,
  isSingleBorrower: true,
};

export const loanReducer = createFeature({
  name: featureName,
  reducer: createReducer(
    initialState,

    // #region getCatalogActions
    on(fromActions.getCatalogActions.load, (state: PpiSimulatorState): PpiSimulatorState => ({
      ...state,
      catalogLoadingStatus: {
        ...initialStatus,
        isLoading: true,
      },
    })),
    on(
      fromActions.getCatalogActions.loadCompleted,
      (state: PpiSimulatorState, { catalog }): PpiSimulatorState => ({
        ...state,
        catalogLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        catalog,
      }),
    ),
    on(fromActions.getCatalogActions.loadFailed, (state, { error }): PpiSimulatorState => ({
      ...state,
      catalogLoadingStatus: {
        ...initialStatus,
        error,
      },
    })),
    // #endregion

    // #region getPublishablesAreasActions
    on(
      fromActions.getPublishablesAreasActions.loadCompleted,
      (state: PpiSimulatorState, { areas }): PpiSimulatorState => ({
        ...state,
        areas,
      }),
    ),
    // #endregion

    // #region createLoanActions
    on(
      fromActions.createLoanActions.post,
      (state: PpiSimulatorState, { loanItemId, userChoices }): PpiSimulatorState => ({
        ...state,
        loanCreationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
        calculationByMonthlyPayment: !userChoices.byDuration,
        selectedProjectType: loanItemId,
      }),
    ),
    on(
      fromActions.createLoanActions.postCompleted,
      (state: PpiSimulatorState, { creationResponse }): PpiSimulatorState => ({
        ...state,
        loanCreationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        creationResponse,
      }),
    ),
    on(
      fromActions.createLoanActions.postFailed,
      (state, { errorCode, errorMsg }): PpiSimulatorState => ({
        ...state,
        loanCreationLoadingStatus: {
          ...initialStatus,
          error: { errorCode, errorMsg },
        },
      }),
    ),
    // #endregion

    // #region getSimulationForConnectedCustomerActions
    on(
      fromActions.getSimulationForConnectedCustomerActions.load,
      (state: PpiSimulatorState): PpiSimulatorState => ({
        ...state,
        connectedSimulationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
      }),
    ),
    on(
      fromActions.getSimulationForConnectedCustomerActions.loadCompleted,
      (state: PpiSimulatorState, { connSimulResponse }): PpiSimulatorState => ({
        ...state,
        connectedSimulationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        simulationCharacteristics: {
          simulationReference: connSimulResponse.simulations[0]?.simulationReference,
          isEmployeeOffer: connSimulResponse.isCollaboratorOffer,
          monthlyPayment: connSimulResponse.simulations[0]?.monthlyPaymentExcludingInsurance,
          duration: connSimulResponse.simulations[0]?.loanDuration,
          tnc: connSimulResponse.simulations[0]?.tnc,
          annualPercentageRateOfCharge:
            connSimulResponse.simulations[0]?.annualPercentageRateOfCharge,
          interests: connSimulResponse.simulations[0]?.interests,
          applicationFees: connSimulResponse.simulations[0]?.applicationFees,
          totalAmount: connSimulResponse.simulations[0]?.totalAmount,
          isLoanEligible: connSimulResponse.simulations[0]?.isLoanEligible,
          isPromotionalOffer: connSimulResponse.simulations[0]?.isPromotionalOffer,
        },
        isConnected: true,
      }),
    ),
    on(
      fromActions.getSimulationForConnectedCustomerActions.loadFailed,
      (state, { error }): PpiSimulatorState => ({
        ...state,
        connectedSimulationLoadingStatus: {
          ...initialStatus,
          error,
        },
      }),
    ),
    // #endregion

    // #region getSimulationForNotConnectedUserActions
    on(
      fromActions.getSimulationForNotConnectedUserActions.load,
      (state: PpiSimulatorState): PpiSimulatorState => ({
        ...state,
        notConnectedSimulationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
        loanCreationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
      }),
    ),
    on(
      fromActions.getSimulationForNotConnectedUserActions.loadCompleted,
      (state: PpiSimulatorState, { notConnSimulResponse }): PpiSimulatorState => ({
        ...state,
        notConnectedSimulationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        simulationCharacteristics: {
          simulationReference: '-',
          monthlyPayment: notConnSimulResponse.monthlyPaymentExcludingInsurance,
          tnc: notConnSimulResponse.tnc,
          duration: notConnSimulResponse.loanDuration,
          annualPercentageRateOfCharge: notConnSimulResponse.annualPercentageRateOfCharge,
          interests: notConnSimulResponse.agios,
          applicationFees: notConnSimulResponse.applicationFees,
          totalAmount: notConnSimulResponse.totalCost,
          isLoanEligible: notConnSimulResponse.isValidatedSimulation,
          isPromotionalOffer: notConnSimulResponse.isPromotionalOffer,
        },
        isConnected: false,
      }),
    ),
    on(
      fromActions.getSimulationForNotConnectedUserActions.loadFailed,
      (state, { error }): PpiSimulatorState => ({
        ...state,
        notConnectedSimulationLoadingStatus: {
          ...initialStatus,
          error,
        },
      }),
    ),
    // #endregion

    // #region saveSimulationActions
    on(
      fromActions.saveSimulationActions.load,
      (state: PpiSimulatorState, props): PpiSimulatorState => ({
        ...state,
        saveSimulationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
        isSingleBorrower: props.isSingleBorrower,
      }),
    ),
    on(
      fromActions.saveSimulationActions.loadCompleted,
      (state: PpiSimulatorState): PpiSimulatorState => ({
        ...state,
        saveSimulationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
      }),
    ),
    on(fromActions.saveSimulationActions.loadFailed, (state, { error }): PpiSimulatorState => ({
      ...state,
      saveSimulationLoadingStatus: {
        ...initialStatus,
        error,
      },
    })),
    // #endregion

    // #region jointCaseCreateLoanActions
    on(
      fromActions.jointCaseCreateLoanActions.load,
      (state: PpiSimulatorState): PpiSimulatorState => ({
        ...state,
        jointCaseLoanCreationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
      }),
    ),
    on(
      fromActions.jointCaseCreateLoanActions.loadCompleted,
      (state: PpiSimulatorState, { creationResponse }): PpiSimulatorState => ({
        ...state,
        jointCaseLoanCreationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        jointCaseCreationResponse: creationResponse,
      }),
    ),
    on(
      fromActions.jointCaseCreateLoanActions.loadFailed,
      (state, { errorCode, errorMsg }): PpiSimulatorState => ({
        ...state,
        jointCaseLoanCreationLoadingStatus: {
          ...initialStatus,
          error: { errorCode, errorMsg },
        },
      }),
    ),
    // #endregion

    // #region jointCaseGetSimulationForConnectedCustomerActions
    on(
      fromActions.jointCaseGetSimulationActions.load,
      (state: PpiSimulatorState): PpiSimulatorState => ({
        ...state,
        jointCaseGetSimulationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
      }),
    ),
    on(
      fromActions.jointCaseGetSimulationActions.loadCompleted,
      (state: PpiSimulatorState, { connSimulResponse }): PpiSimulatorState => ({
        ...state,
        jointCaseGetSimulationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        jointCaseSimulationCharacteristics: {
          simulationReference: connSimulResponse.simulations[0]?.simulationReference,
          isEmployeeOffer: connSimulResponse.isCollaboratorOffer,
          monthlyPayment: connSimulResponse.simulations[0]?.monthlyPaymentExcludingInsurance,
          duration: connSimulResponse.simulations[0]?.loanDuration,
          tnc: connSimulResponse.simulations[0]?.tnc,
          annualPercentageRateOfCharge:
            connSimulResponse.simulations[0]?.annualPercentageRateOfCharge,
          interests: connSimulResponse.simulations[0]?.interests,
          applicationFees: connSimulResponse.simulations[0]?.applicationFees,
          totalAmount: connSimulResponse.simulations[0]?.totalAmount,
          isLoanEligible: connSimulResponse.simulations[0]?.isLoanEligible,
          isPromotionalOffer: connSimulResponse.simulations[0]?.isPromotionalOffer,
        },
        isConnected: true,
      }),
    ),
    on(
      fromActions.jointCaseGetSimulationActions.loadFailed,
      (state, { error }): PpiSimulatorState => ({
        ...state,
        jointCaseGetSimulationLoadingStatus: {
          ...initialStatus,
          error,
        },
      }),
    ),
    // #endregion

    // #region createLoanPostConnectionActions
    on(
      fromActions.createLoanPostConnectionActions.load,
      (state: PpiSimulatorState, { loanItemId, userChoices }): PpiSimulatorState => ({
        ...state,
        loanCreationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
        calculationByMonthlyPayment: !userChoices.byDuration,
        selectedProjectType: loanItemId,
      }),
    ),
    on(
      fromActions.createLoanPostConnectionActions.loadCompleted,
      (state: PpiSimulatorState, { creationResponse }): PpiSimulatorState => ({
        ...state,
        loanCreationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        creationResponse,
      }),
    ),
    on(
      fromActions.createLoanPostConnectionActions.loadFailed,
      (state, { errorCode, errorMsg }): PpiSimulatorState => ({
        ...state,
        loanCreationLoadingStatus: {
          ...initialStatus,
          error: { errorCode, errorMsg },
        },
      }),
    ),
    // #endregion

    // #region getSimulationForConnectedCustomerPostConnectionActions
    on(
      fromActions.getSimulationForConnectedCustomerPostConnectionActions.load,
      (state: PpiSimulatorState): PpiSimulatorState => ({
        ...state,
        connectedSimulationLoadingStatus: {
          ...initialStatus,
          isLoading: true,
        },
      }),
    ),
    on(
      fromActions.getSimulationForConnectedCustomerPostConnectionActions.loadCompleted,
      (state: PpiSimulatorState, { connSimulResponse }): PpiSimulatorState => ({
        ...state,
        connectedSimulationLoadingStatus: {
          ...initialStatus,
          isLoaded: true,
        },
        simulationCharacteristics: {
          simulationReference: connSimulResponse.simulations[0]?.simulationReference,
          isEmployeeOffer: connSimulResponse.isCollaboratorOffer,
          monthlyPayment: connSimulResponse.simulations[0]?.monthlyPaymentExcludingInsurance,
          duration: connSimulResponse.simulations[0]?.loanDuration,
          tnc: connSimulResponse.simulations[0]?.tnc,
          annualPercentageRateOfCharge:
            connSimulResponse.simulations[0]?.annualPercentageRateOfCharge,
          interests: connSimulResponse.simulations[0]?.interests,
          applicationFees: connSimulResponse.simulations[0]?.applicationFees,
          totalAmount: connSimulResponse.simulations[0]?.totalAmount,
          isLoanEligible: connSimulResponse.simulations[0]?.isLoanEligible,
          isPromotionalOffer: connSimulResponse.simulations[0]?.isPromotionalOffer,
        },
        isConnected: true,
      }),
    ),
    on(
      fromActions.getSimulationForConnectedCustomerPostConnectionActions.loadFailed,
      (state, { error }): PpiSimulatorState => ({
        ...state,
        connectedSimulationLoadingStatus: {
          ...initialStatus,
          error,
        },
      }),
    ),
    // #endregion
  ),
});
